/**
 * @file 本地数据存储
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/storage
 */
import isClient from './detectClient';
import {detectStorage} from './detectStorageAvailability';

/**
 * 存储到 localStorage 的键名
 *
 * @type {string}
 */
const KEY = 'HABO_UNSENTD';

/**
 * storage 代理，某些手机浏览器禁用localStorage，需要做降级处理
 */
const storage = (useLocalStorage => {
    if (detectStorage()) {
        return localStorage;
    }

    const data = Object.create(null);
    return {
        setItem(key, value) {
            data[key] = String(value);
        },
        getItem(key) {
            return data[key];
        },
        removeItem(key) {
            delete data[key];
        }
    };
})(isClient);

/**
 * 读取已存储的未发送 URL
 *
 * @return {string[]} 返回未发送的请求 URL 数组
 */
const read = () =>
    (isClient && storage.getItem(KEY) || '')
        .split(',')
        .filter(Boolean)
        .reduce((map, url) => {
            const [key, data] = url.split('|');
            map[key] = data ? JSON.parse(decodeURIComponent(data)) : '';

            return map;
        }, {});

/**
 * 读取到的未发送 URL 数组
 *
 * @type {Object.<string, string | string[]>}
 */
const URLS = read();

const PENDINGS = {};

/**
 * 存储未发送的 URL
 *
 * @param {?string} url 待存储的 URL
 * @param {?Array=} data 待存储的 post 数据
 */
const save = (url, data) => {
    if (url && (!PENDINGS[url] || data && URLS[url] !== data)) {
        let value = URLS[url] || '';

        if (data && data.length) {
            data = Array.isArray(data) ? data : [data];
            value = Array.isArray(value) ? value.concat(data) : data;
        }

        URLS[url] = value;
    }

    try {
        const savedData = Object.keys(URLS).reduce((list, key) => {
            list.push(URLS[key] ? `${key}|${encodeURIComponent(JSON.stringify(URLS[key]))}` : key);

            return list;
        }, []);

        isClient && storage.setItem(KEY, savedData.join(','));
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error(e.message);
    }
};

const pending = url => (PENDINGS[url] = true);
const deletePending = url => (PENDINGS[url] = false);

/**
 * 移除已发送的 URL
 *
 * @param {string} url 要移除的 URL
 * @param {boolean} 移除成功与否
 */
const remove = url => {
    if (!(url in URLS) && !PENDINGS[url]) {
        return false;
    }

    delete PENDINGS[url];
    delete URLS[url];
    save();

    return true;
};

/**
 * 清除存储数据
 */
const clear = (reset = false) => {
    Object.keys(URLS).forEach(url => delete URLS[url]);

    if (reset) {
        Object.keys(PENDINGS).forEach(url => delete PENDINGS[url]);
    }

    isClient && storage.removeItem(KEY);
};

/**
 * 重复发送未完成的请求
 *
 * @param {Function} request 发送请求的方法
 */
const resend = request => {
    if (!Object.keys(URLS).length) {
        return;
    }

    const params = Object.keys(URLS)
        .filter(url => !PENDINGS[url])
        .map(url => {
            PENDINGS[url] = true;

            const args = [url];
            // 一般 data 是空字符串
            const data = URLS[url];

            if (data) {
                args.push('', null, data);
            }

            return args;
        });

    clear();

    params.forEach(args => request(...args));

};

export default {
    read,
    save,
    remove,
    resend,
    clear,
    pending,
    deletePending
};
